import { useState, useMemo } from "react";
import StepNav, { DENTAL_STEPS } from "@/components/StepNav";
import { toast } from "sonner";
import {
  ChevronLeft,
  Trash2,
  FileDown,
  CheckCircle2,
  Stethoscope,
  ArrowRight,
  Ruler,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DENTITION,
  SYMBOLS,
  DENTAL_CASES,
  toothType,
  gradeCase,
  satisfiedCount,
  perioSummary,
} from "@/data/dentalCases";
import { ToothArcade } from "@/components/ToothArcade";
import { SymbolChip } from "@/components/dental/SymbolChip";
import { ResultPanel } from "@/components/dental/ResultPanel";
import { PocketDiagram, RadiographToggle, AvdcQuiz, HomeCareCard } from "@/components/dental/DentalExtras";
import { SaveSession } from "@/components/SaveSession";
import { exportDentalPDF } from "@/components/dental/dentalPdf";
import { FurtherReading } from "@/components/FurtherReading";
import { useCaseList } from "@/context/ContentContext";

const ACCENT = "#0d9488"; // teal — dental module accent

const DIFF_STYLE = {
  Beginner: "bg-emerald-100 text-emerald-700",
  Intermediate: "bg-amber-100 text-amber-700",
  Advanced: "bg-rose-100 text-rose-700",
};

/* ---------------- small pieces ---------------- */
function Tooth({ id, symbols, perio, selected, onClick }) {
  const has = symbols.length > 0;
  const perioStr = perioSummary(perio);
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={`tooth-${id}-btn`}
      className={`relative flex flex-col items-center rounded-md border-2 px-1 pt-1 pb-1.5 min-w-[36px] transition-all ${
        selected
          ? "border-[#0d9488] ring-2 ring-[#0d9488]/25 bg-[#0d9488]/5"
          : has || perioStr
          ? "border-slate-300 bg-slate-50 hover:border-slate-400"
          : "border-slate-200 bg-white hover:border-slate-300"
      }`}
    >
      <span className="text-[11px] font-mono font-semibold text-slate-700 leading-none">{id}</span>
      <span className="text-[8px] uppercase tracking-wide text-slate-400 mt-0.5">{toothType(id)}</span>
      <span className="flex flex-wrap gap-0.5 justify-center mt-1 min-h-[12px] max-w-[40px]">
        {symbols.map((c) => (
          <SymbolChip key={c} code={c} />
        ))}
      </span>
      {perioStr && (
        <span
          className="mt-0.5 text-[7px] font-semibold leading-none px-1 py-0.5 rounded bg-amber-100 text-amber-700 whitespace-nowrap"
          data-testid={`tooth-${id}-perio`}
        >
          {perioStr}
        </span>
      )}
    </button>
  );
}

const ToothRow = ({ teeth, chart, perioChart, selectedTooth, onTooth }) => (
  <div className="flex gap-1">
    {teeth.map((id) => (
      <Tooth
        key={id}
        id={id}
        symbols={chart[id] || []}
        perio={perioChart[id]}
        selected={selectedTooth === id}
        onClick={() => onTooth(id)}
      />
    ))}
  </div>
);

/* ---------------- case selection ---------------- */
function CaseGrid({ cases, onStart }) {
  return (
    <main className="max-w-6xl mx-auto px-6 py-12 fade-up">
      <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] font-semibold mb-2" style={{ color: ACCENT }}>
        <Stethoscope className="w-3.5 h-3.5" />
        <span>Dental charting · Modified Triadan</span>
      </div>
      <h1 className="text-4xl sm:text-5xl font-bold tracking-tight text-slate-900">Choose a case</h1>
      <p className="text-base text-slate-600 mt-3 max-w-2xl">
        Read the briefing, then mark up the dental chart accurately — identify the correct tooth and
        apply the correct AVDC symbol. Graded at the end.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 mt-10">
        {cases.map((c) => (
          <button
            key={c.id}
            type="button"
            onClick={() => onStart(c.id)}
            data-testid={`dental-case-${c.id}`}
            className="group text-left bg-white border border-slate-200 rounded-xl p-5 hover:border-[#0d9488] hover:shadow-lg hover:-translate-y-1 transition-all duration-300"
          >
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">
                {c.species === "cat" ? "Feline" : "Canine"} · {c.title}
              </span>
              <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${DIFF_STYLE[c.difficulty]}`}>
                {c.difficulty}
              </span>
            </div>
            <h2 className="text-lg font-semibold text-slate-900">{c.name}</h2>
            <div className="text-xs text-slate-500 mb-2">
              {c.breed} · {c.age} · {DENTITION[c.species].teeth}-tooth dentition
            </div>
            <p className="text-sm text-slate-600 leading-relaxed line-clamp-3">{c.briefing}</p>
            <div className="flex items-center justify-between mt-4">
              <span className="text-[11px] font-semibold text-slate-500">
                {c.findings.length} findings to chart
                {c.perio?.length ? ` · ${c.perio.length}-tooth perio probe` : ""}
              </span>
              <span className="flex items-center gap-1 text-sm font-semibold" style={{ color: ACCENT }}>
                Start case <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </span>
            </div>
          </button>
        ))}
      </div>
    </main>
  );
}

/* ---------------- main page ---------------- */
export default function Dental() {
  const cases = useCaseList("dental", DENTAL_CASES);
  const [caseId, setCaseId] = useState(null);
  const [chart, setChart] = useState({});
  const [perioChart, setPerioChart] = useState({});
  const [selectedTooth, setSelectedTooth] = useState(null);
  const [selectedSymbol, setSelectedSymbol] = useState(null);
  const [result, setResult] = useState(null);

  const dentalCase = useMemo(() => cases.find((c) => c.id === caseId), [caseId, cases]);
  const dent = dentalCase ? DENTITION[dentalCase.species] : null;
  const satisfied = dentalCase ? satisfiedCount(dentalCase, chart) : 0;

  const startCase = (id) => {
    setCaseId(id);
    setChart({});
    setPerioChart({});
    setSelectedTooth(null);
    setSelectedSymbol(null);
    setResult(null);
  };

  const backToCases = () => {
    setCaseId(null);
    setResult(null);
  };

  const applyToTooth = (tooth, code) => {
    setResult(null);
    setChart((prev) => {
      const cur = new Set(prev[tooth] || []);
      if (cur.has(code)) cur.delete(code);
      else cur.add(code);
      const next = { ...prev };
      if (cur.size) next[tooth] = [...cur];
      else delete next[tooth];
      return next;
    });
  };

  const onTooth = (tooth) => {
    if (selectedSymbol) {
      // brush mode: stamp the active symbol, keep the brush armed for more teeth
      applyToTooth(tooth, selectedSymbol);
    } else {
      // selection mode: pick a tooth, then toggle multiple issues / probe perio on it
      setSelectedTooth((t) => (t === tooth ? null : tooth));
    }
  };

  const onSymbol = (code) => {
    if (selectedTooth) {
      // a tooth is selected → toggle this issue on it (lets you stack multiple issues)
      applyToTooth(selectedTooth, code);
    } else {
      // no tooth selected → arm this symbol as a brush to paint multiple teeth
      setSelectedSymbol((c) => (c === code ? null : code));
    }
  };

  const setPerio = (field, value) => {
    if (!selectedTooth) return;
    setResult(null);
    setPerioChart((prev) => {
      const cur = { ...(prev[selectedTooth] || {}) };
      if (value == null) delete cur[field];
      else cur[field] = value;
      const next = { ...prev };
      if (Object.keys(cur).length) next[selectedTooth] = cur;
      else delete next[selectedTooth];
      return next;
    });
  };

  const clearPerio = () => {
    if (!selectedTooth) return;
    setResult(null);
    setPerioChart((prev) => {
      const next = { ...prev };
      delete next[selectedTooth];
      return next;
    });
  };

  const clearChart = () => {
    setChart({});
    setPerioChart({});
    setResult(null);
    setSelectedTooth(null);
    toast.success("Chart cleared.");
  };

  const submit = () => {
    if (Object.keys(chart).length === 0 && Object.keys(perioChart).length === 0) {
      toast.error("Chart is empty — apply some findings first.");
      return;
    }
    const r = gradeCase(dentalCase, chart, perioChart);
    setResult(r);
    setSelectedTooth(null);
    setSelectedSymbol(null);
    toast.success(`Chart graded — ${r.grade} (${r.pct}%)`);
  };

  const exportPDF = () => exportDentalPDF(dentalCase, chart, perioChart, result);

  /* ---------- render ---------- */
  return (
    <div className="min-h-screen bg-[#F4F7F9]">
      <StepNav
        current={1}
        steps={DENTAL_STEPS}
        title="Dental"
        subtitle="Modified Triadan charting"
      />

      {!dentalCase ? (
        <CaseGrid cases={cases} onStart={startCase} />
      ) : (
        <main className="max-w-6xl mx-auto px-6 py-8 fade-up">
          {/* title + controls */}
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4 mb-6">
            <div>
              <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] font-semibold" style={{ color: ACCENT }}>
                <Stethoscope className="w-3.5 h-3.5" /> Charting in progress
              </div>
              <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 mt-1">
                {dentalCase.name} · {dentalCase.species === "cat" ? "Feline" : "Canine"} · {dentalCase.title}
              </h1>
              <p className="text-sm text-slate-500 mt-1">
                {dentalCase.breed} · {dentalCase.age} · {dentalCase.species === "cat" ? "Feline" : "Canine"} dentition ({dent.teeth} teeth)
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <Button size="sm" variant="outline" onClick={backToCases} data-testid="back-to-cases-btn" className="text-xs">
                <ChevronLeft className="w-3.5 h-3.5 mr-1" /> Cases
              </Button>
              <Button size="sm" variant="outline" onClick={clearChart} data-testid="clear-chart-btn" className="text-xs">
                <Trash2 className="w-3.5 h-3.5 mr-1" /> Clear
              </Button>
              <Button size="sm" variant="outline" onClick={exportPDF} data-testid="export-dental-pdf-btn" className="text-xs">
                <FileDown className="w-3.5 h-3.5 mr-1" /> PDF
              </Button>
              <Button size="sm" onClick={submit} data-testid="submit-chart-btn" className="text-xs text-white ml-auto lg:ml-0" style={{ background: ACCENT }}>
                <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Submit chart
              </Button>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* LEFT: chart + palette */}
            <div className="lg:col-span-2 space-y-6">
              {/* anatomical arcade (occlusal) — shares state with the geometric grid */}
              <div className="bg-white border border-slate-200 rounded-xl p-5 overflow-x-auto" data-testid="arcade-chart-card">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">
                    Anatomical arcade · occlusal view
                  </span>
                  <span className="text-[10px] text-slate-400">
                    click teeth here or on the grid below — both stay in sync
                  </span>
                </div>
                <ToothArcade
                  species={dentalCase.species}
                  chart={chart}
                  perioChart={perioChart}
                  selectedTooth={selectedTooth}
                  onTooth={onTooth}
                />
              </div>

              {/* dental chart */}
              <div className="bg-white border border-slate-200 rounded-xl p-5 overflow-x-auto" data-testid="dental-chart">
                <ChartRow
                  label="Maxillary (upper)"
                  right={dent.UR}
                  left={dent.UL}
                  chart={chart}
                  perioChart={perioChart}
                  selectedTooth={selectedTooth}
                  onTooth={onTooth}
                />
                <div className="flex items-center gap-3 my-3 text-[10px] uppercase tracking-widest text-slate-400">
                  <div className="flex-1 h-px bg-slate-200" />
                  Midline
                  <div className="flex-1 h-px bg-slate-200" />
                </div>
                <ChartRow
                  label="Mandibular (lower)"
                  right={dent.LR}
                  left={dent.LL}
                  chart={chart}
                  perioChart={perioChart}
                  selectedTooth={selectedTooth}
                  onTooth={onTooth}
                />
              </div>

              {/* symbol palette */}
              <SymbolPalette
                selectedTooth={selectedTooth}
                selectedSymbol={selectedSymbol}
                setSelectedTooth={setSelectedTooth}
                setSelectedSymbol={setSelectedSymbol}
                onSymbol={onSymbol}
              />

              {dentalCase.perio?.length ? (
                <PerioPanel
                  tooth={selectedTooth}
                  value={selectedTooth ? perioChart[selectedTooth] : null}
                  onSet={setPerio}
                  onClear={clearPerio}
                />
              ) : null}

              <AvdcQuiz symbols={SYMBOLS} />
              <HomeCareCard />
            </div>

            {/* RIGHT: briefing / result */}
            <div className="space-y-6">
              {result ? (
                <ResultPanel
                  result={result}
                  onRetry={() => startCase(caseId)}
                  onCases={backToCases}
                />
              ) : (
                <BriefingCard dentalCase={dentalCase} chart={chart} satisfied={satisfied} />
              )}

              <RadiographToggle />
            </div>
          </div>

          {result && (
            <SaveSession
              simulatorId="dental"
              simulatorName="Dental Charting Trainer"
              score={result.pct}
              grade={result.grade}
              summary={`Grade ${result.grade} · ${result.pct}% accuracy`}
            />
          )}

          <FurtherReading topic="dental" className="mt-6" />
        </main>
      )}
    </div>
  );
}

/* a maxillary/mandibular row = right quadrant · midline · left quadrant */
const ChartRow = ({ label, right, left, chart, perioChart, selectedTooth, onTooth }) => (
  <div>
    <div className="flex items-center justify-between mb-2">
      <span className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{label}</span>
      <span className="text-[10px] text-slate-400 font-mono">patient&apos;s right ← · → patient&apos;s left</span>
    </div>
    <div className="flex items-stretch justify-start lg:justify-center gap-2 w-max mx-auto">
      <ToothRow teeth={right} chart={chart} perioChart={perioChart} selectedTooth={selectedTooth} onTooth={onTooth} />
      <div className="w-px bg-slate-200 self-stretch" />
      <ToothRow teeth={left} chart={chart} perioChart={perioChart} selectedTooth={selectedTooth} onTooth={onTooth} />
    </div>
  </div>
);

/* AVDC symbol palette + active-mode banner */
function SymbolPalette({ selectedTooth, selectedSymbol, setSelectedTooth, setSelectedSymbol, onSymbol }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid="symbol-palette">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">AVDC symbol palette</h3>
      </div>
      {selectedTooth ? (
        <div data-testid="chart-mode-tooth" className="flex items-center justify-between gap-2 mb-3 text-[12px] rounded-md px-3 py-2 bg-amber-50 border border-amber-200 text-amber-800">
          <span>Tooth <b className="font-mono">{selectedTooth}</b> selected — click symbols to add or remove issues.</span>
          <button type="button" data-testid="deselect-tooth-btn" onClick={() => setSelectedTooth(null)} className="shrink-0 font-semibold text-amber-700 hover:text-amber-900">Done</button>
        </div>
      ) : selectedSymbol ? (
        <div data-testid="chart-mode-brush" className="flex items-center justify-between gap-2 mb-3 text-[12px] rounded-md px-3 py-2 bg-[#0d9488]/5 border border-[#0d9488]/30 text-[#0f766e]">
          <span>Brush <b>{selectedSymbol}</b> active — click teeth to chart them.</span>
          <button type="button" data-testid="clear-brush-btn" onClick={() => setSelectedSymbol(null)} className="shrink-0 font-semibold hover:underline">Done</button>
        </div>
      ) : (
        <p className="text-[11px] text-slate-400 mb-3">Click a tooth to select it, then add issues — or pick a symbol to paint multiple teeth.</p>
      )}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
        {SYMBOLS.map((s) => (
          <button
            key={s.code}
            type="button"
            onClick={() => onSymbol(s.code)}
            data-testid={`symbol-${s.code}-btn`}
            className={`flex items-start gap-2 rounded-md border-2 px-2.5 py-2 text-left transition-all ${
              selectedSymbol === s.code
                ? "border-[#0d9488] bg-[#0d9488]/5"
                : "border-slate-200 hover:border-slate-300"
            }`}
          >
            <span
              className="shrink-0 w-7 h-7 rounded flex items-center justify-center text-white text-xs font-bold"
              style={{ background: s.color }}
            >
              {s.code}
            </span>
            <span className="min-w-0">
              <span className="block text-xs font-semibold text-slate-800 leading-tight">{s.name}</span>
              <span className="block text-[10px] text-slate-500 leading-tight">{s.desc}</span>
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}

/* patient briefing + findings checklist (shown until a result exists) */
function BriefingCard({ dentalCase, chart, satisfied }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid="findings-card">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">Patient briefing</h3>
        <span
          className="text-xs font-mono font-semibold px-2 py-0.5 rounded-full text-white"
          style={{ background: satisfied === dentalCase.findings.length ? ACCENT : "#94a3b8" }}
          data-testid="findings-progress"
        >
          {satisfied}/{dentalCase.findings.length}
        </span>
      </div>
      <p className="text-sm text-slate-600 leading-relaxed bg-slate-50 rounded-md p-3 border border-slate-100">
        {dentalCase.briefing}
      </p>
      <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold mt-4 mb-2">
        Findings to chart
      </div>
      <ul className="space-y-2">
        {dentalCase.findings.map((f, i) => {
          const have = new Set(chart[f.tooth] || []);
          const done = f.symbols.every((s) => have.has(s));
          return (
            <li key={`${f.tooth}-${i}`} className="flex items-start gap-2.5 text-[13px]" data-testid={`finding-${i}`}>
              <span
                className={`shrink-0 w-5 h-5 rounded-full flex items-center justify-center text-[11px] font-bold mt-0.5 ${
                  done ? "text-white" : "bg-slate-100 text-slate-500"
                }`}
                style={done ? { background: ACCENT } : undefined}
              >
                {done ? "✓" : i + 1}
              </span>
              <span className={done ? "text-slate-400 line-through" : "text-slate-700"}>{f.note}</span>
            </li>
          );
        })}
      </ul>
      <p className="text-[11px] text-slate-400 mt-4 leading-relaxed">
        Tip: incisor/canine/premolar/molar map to Triadan positions; the right of the chart is the
        patient&apos;s left side.
      </p>
    </div>
  );
}

/* periodontal exam panel — pocket depth + gingivitis index for the selected tooth */
function PerioPanel({ tooth, value, onSet, onClear }) {
  const v = value || {};
  const IndexButtons = ({ label, field }) => (
    <div className="flex items-center justify-between">
      <span className="text-xs font-medium text-slate-600">{label}</span>
      <div className="flex gap-1">
        {[0, 1, 2, 3].map((n) => (
          <button
            key={n}
            type="button"
            data-testid={`perio-${field}-${n}-btn`}
            onClick={() => onSet(field, v[field] === n ? null : n)}
            className={`w-7 h-7 rounded text-xs font-semibold border transition-colors ${
              v[field] === n ? "text-white border-transparent" : "bg-white border-slate-200 text-slate-600 hover:border-slate-300"
            }`}
            style={v[field] === n ? { background: ACCENT } : undefined}
          >
            {n}
          </button>
        ))}
      </div>
    </div>
  );
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid="perio-panel">
      <div className="flex items-center justify-between mb-3">
        <h3 className="flex items-center gap-1.5 text-[11px] uppercase tracking-widest text-slate-500 font-semibold">
          <Ruler className="w-3.5 h-3.5" /> Periodontal exam
        </h3>
        {tooth ? (
          <span className="text-[11px] font-mono font-semibold text-white px-2 py-0.5 rounded-full" style={{ background: ACCENT }} data-testid="perio-active-tooth">
            Tooth {tooth}
          </span>
        ) : (
          <span className="text-[11px] text-slate-400">Select a tooth</span>
        )}
      </div>

      {!tooth ? (
        <p className="text-xs text-slate-400 bg-slate-50 rounded-md p-3 border border-slate-100">
          Click a tooth on the chart (with no symbol selected) to record its pocket depth and indices.
        </p>
      ) : (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium text-slate-600">Pocket depth (mm)</span>
            <div className="flex items-center gap-2">
              <button type="button" data-testid="perio-pocket-dec-btn" onClick={() => onSet("pocket", Math.max(0, (v.pocket ?? 0) - 1))} className="w-7 h-7 rounded border border-slate-200 text-slate-600 hover:border-slate-300 font-bold leading-none">−</button>
              <span className="w-10 text-center text-sm font-mono font-semibold text-slate-800" data-testid="perio-pocket-value">{v.pocket ?? "—"}</span>
              <button type="button" data-testid="perio-pocket-inc-btn" onClick={() => onSet("pocket", Math.min(12, (v.pocket ?? 0) + 1))} className="w-7 h-7 rounded border border-slate-200 text-slate-600 hover:border-slate-300 font-bold leading-none">+</button>
            </div>
          </div>
          <IndexButtons label="Gingivitis (GI)" field="gi" />
          <PocketDiagram pocket={v.pocket} gi={v.gi} />
          <button type="button" data-testid="perio-clear-btn" onClick={onClear} className="text-[11px] text-rose-500 hover:text-rose-600 font-medium">
            Clear this tooth&apos;s periodontal reading
          </button>
        </div>
      )}
    </div>
  );
}
